
import { ITotalCartItems } from "../cart/types";
import { ISelectedSubscr, IProfileData } from "../subscriptions/types";
import { ISelectedHall } from "../halls/types";
import { IPaymentItem } from "./bitrixPayment";
import { cleanPhone } from "../../utils/form/phoneFormat";

export const convertCartToBitrixItems = (
  cartItems: ITotalCartItems,
  partnerId: string
): IPaymentItem[] => {
  const items: IPaymentItem[] = [];

  Object.values(cartItems).forEach((category) => {
    Object.values(category).forEach((item) => {
      if (isSelectedSubscr(item)) {
        const subscr = item as ISelectedSubscr;
        const subscription = subscr.subscription;

        if (subscription.type === 'family' && subscr.profiles && subscr.profiles.length > 0) {
          const firstProfile = subscr.profiles[0];
          
          const profilePartnerId = firstProfile.partner_id ? String(firstProfile.partner_id) : partnerId;
          
          const totalPeople = Array.isArray(subscr.quantity) 
            ? subscr.quantity.reduce((a: number, b: number) => a + b, 0)
            : (typeof subscr.quantity === 'number' ? subscr.quantity : 1);
          
          const basePrice = subscription.basePrice || subscription.price;
          const additionalPersonPrice = subscription.additionalPersonPrice || subscription.surchargeAdult || 1000;
          const minPeople = subscription.minPeopleRequired || 3;
          
          const calculatedPrice = totalPeople >= minPeople
            ? basePrice + (totalPeople - minPeople) * additionalPersonPrice
            : basePrice;
          
          const paymentItem: IPaymentItem = {
            itemType: 'membership',
            membershipType: 'family',
            membershipId: String(subscription.id),
            count: totalPeople,
            sum: calculatedPrice,
            profileName: firstProfile.name,
            partnerId: profilePartnerId,
          };

          if (subscr.profiles.length > 1) {
            paymentItem.additionalOwners = subscr.profiles
              .slice(1)
              .map((p: IProfileData) => ({ 
                partnerId: String(p.partner_id || '') 
              }))
              .filter((o: { partnerId: string }) => o.partnerId && o.partnerId !== '');
          }

          items.push(paymentItem);
        } else {
          subscr.profiles.forEach((profile: IProfileData) => {
            const profilePartnerId = profile.partner_id ? String(profile.partner_id) : partnerId;
            
            const paymentItem: IPaymentItem = {
              itemType: 'membership',
              membershipType: subscription.type as 'adult' | 'kid' | 'gift',
              membershipId: String(subscription.id),
              count: typeof subscr.quantity === 'number' ? subscr.quantity : 1,
              sum: subscription.price * (typeof subscr.quantity === 'number' ? subscr.quantity : 1),
              profileName: profile.name,
              partnerId: profilePartnerId,
            };

            if (subscription.type === 'adult' && profile.secondaryText) {
              paymentItem.phone = cleanPhone(profile.secondaryText);
            } else if (subscription.type === 'kid' && profile.secondaryText) {
              paymentItem.birthday = profile.secondaryText;
            }

            if (subscription.isGift || (subscription as any).type === 'gift') {
              paymentItem.membershipType = 'gift';
              
              if (profile.email) {
                paymentItem.email = profile.email;
              }
              
              if ((profile as any).message) {
                paymentItem.message = (profile as any).message;
              }
              
              if ((subscr as any).deliveryDate) {
                const deliveryDate = (subscr as any).deliveryDate;
                try {
                  const parts = deliveryDate.split('.');
                  if (parts.length === 3) {
                    const isoDate = `${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`;
                    paymentItem.giftDate = isoDate;
                  }
                } catch (e) {
                }
              }
            }

            items.push(paymentItem);
          });
        }
      } else if (isSelectedHall(item)) {
        const hall = item as ISelectedHall;
        
        let startTime = hall.startTime;
        let finishTime = hall.finishTime;
        
        if (!startTime || !finishTime) {
          startTime = convertTimeToISO(hall.date, hall.time);
          finishTime = convertTimeToISO(hall.date, hall.time, 1);
        }

        const paymentItem: IPaymentItem = {
          itemType: 'rent',
          courtId: hall.courtId || String(hall.hallId || ''),
          sum: hall.price || 0,
          startTime: startTime,
          finishTime: finishTime,
        };

        items.push(paymentItem);
      }
    });
  });

  return items;
};

const isSelectedSubscr = (item: ISelectedSubscr | ISelectedHall): item is ISelectedSubscr => {
  return 'subscription' in item && 'profiles' in item;
};

const isSelectedHall = (item: ISelectedSubscr | ISelectedHall): item is ISelectedHall => {
  return 'date' in item && 'time' in item && 'price' in item;
};


const convertTimeToISO = (date: string, time: string, addHours: number = 0): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const dateObj = new Date(`${date}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
  
  if (addHours > 0) {
    dateObj.setHours(dateObj.getHours() + addHours);
  }
  
  return dateObj.toISOString();
};

